import { useEffect, useState } from 'react'
import { Button, Card, Input } from '../../components/primitives/index.js'
import { customerApi } from '../../lib/customerApi.js'
import './AccountAddresses.css'

const EMPTY = { label: '', name: '', address: '', apt: '', city: '', state: '', zip: '', country: 'US' }

export default function AccountAddresses() {
  const [addresses, setAddresses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState(EMPTY)

  useEffect(() => {
    customerApi
      .getAddresses()
      .then((res) => setAddresses(res.addresses ?? []))
      .catch((e) => {
        setError(e.message)
        setAddresses([])
      })
      .finally(() => setLoading(false))
  }, [])

  async function run(fn) {
    setSaving(true)
    setError('')
    try {
      const res = await fn()
      setAddresses(res.addresses ?? [])
      setAdding(false)
      setEditingId(null)
      setForm(EMPTY)
    } catch (e) {
      setError(e.message ?? 'Something went wrong.')
    } finally {
      setSaving(false)
    }
  }

  function startEdit(addr) {
    setEditingId(addr.id)
    setAdding(false)
    setForm({ ...EMPTY, ...addr })
  }

  function startAdd() {
    setAdding(true)
    setEditingId(null)
    setForm(EMPTY)
  }

  function cancel() {
    setAdding(false)
    setEditingId(null)
    setForm(EMPTY)
  }

  function update(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (!form.address || !form.city || !form.zip) return
    if (editingId) run(() => customerApi.updateAddress(editingId, form))
    else run(() => customerApi.addAddress(form))
  }

  if (loading) return <Card>Loading addresses&hellip;</Card>

  const showForm = adding || editingId

  return (
    <div className="account-addresses">
      {error && <p className="account-addresses__error">{error}</p>}

      {!showForm && (
        <>
          {addresses.length === 0 && <Card className="account-addresses__empty">No saved addresses yet.</Card>}
          {addresses.map((addr) => (
            <Card key={addr.id} className="account-addresses__card">
              <div className="account-addresses__card-top">
                <span className="account-addresses__label">{addr.label || 'Address'}</span>
                {addr.isDefault && <span className="account-addresses__default">Default</span>}
              </div>
              <p className="account-addresses__lines">
                {addr.name && <>{addr.name}<br /></>}
                {addr.address}
                {addr.apt && <>, {addr.apt}</>}<br />
                {[addr.city, addr.state, addr.zip].filter(Boolean).join(', ')}<br />
                {addr.country}
              </p>
              <div className="account-addresses__actions">
                <Button size="sm" variant="ghost" onClick={() => startEdit(addr)}>
                  Edit
                </Button>
                {!addr.isDefault && (
                  <Button size="sm" variant="ghost" onClick={() => run(() => customerApi.updateAddress(addr.id, { isDefault: true }))}>
                    Make default
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => run(() => customerApi.deleteAddress(addr.id))}>
                  Delete
                </Button>
              </div>
            </Card>
          ))}
          <Button variant="secondary" onClick={startAdd}>
            + Add address
          </Button>
        </>
      )}

      {showForm && (
        <Card>
          <form className="account-addresses__form" onSubmit={handleSubmit}>
            <Input label="Label (optional)" value={form.label} onChange={update('label')} />
            <Input label="Name (optional)" value={form.name} onChange={update('name')} />
            <Input label="Address" required value={form.address} onChange={update('address')} />
            <Input label="Apt / suite (optional)" value={form.apt} onChange={update('apt')} />
            <div className="account-addresses__row">
              <Input label="City" required value={form.city} onChange={update('city')} />
              <Input label="State (optional)" value={form.state} onChange={update('state')} />
              <Input label="ZIP" required value={form.zip} onChange={update('zip')} />
            </div>
            <Input label="Country" value={form.country} onChange={update('country')} />
            <div className="account-addresses__form-actions">
              <Button type="button" variant="ghost" onClick={cancel}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={saving}>
                {editingId ? 'Save changes' : 'Add address'}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  )
}
